import { useEffect, useState, Fragment } from 'react';
import { Form, Button, Spin, Table, Select, Space, InputNumber, Input } from 'antd';
import { Box, Typography, Stack } from '@mui/material';
import { Tabs as AntTabs } from 'antd';
import type { TabsProps } from 'antd';
import {
  FileTextOutlined, CreditCardOutlined, PaperClipOutlined, SafetyCertificateOutlined, TeamOutlined,
  UserAddOutlined, EditOutlined,
} from '@ant-design/icons';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { emphasize, styled } from '@mui/material/styles';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Swal from 'sweetalert2';
import api from '../../../Api/axiosInstance';
import '../../../Styles/StyleHome.css';
import SectionCard from './SectionCard';
import KycClientHeader, { getVal } from './KycClientHeader';
import KycComptesPanel from './KycComptesPanel';
import KycDocumentsPanel from './KycDocumentsPanel';
import EditableValueField from './EditableValueField';
import TypSignature from '../../Tsignature';
import KycImageThumb from './KycImageThumb';
import KycModifierIdentiteModal from './KycModifierIdentiteModal';
import StagedDocumentsPicker, { type StagedFile } from './StagedDocumentsPicker';
import { uploadKycDocument } from './kycDocumentsApi';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import { encryptBody, parseEncryptedResponse, fetchList } from './kycCrypto';
import Data from '../../../Data/Data.json';
import {
  CHAMPS_DATE_KYC,
  CHAMPS_IMAGE_KYC,
  CHAMPS_PAR_TYPE_MODIFICATION,
  CHAMPS_RADIO_KYC,
  CHAMPS_SELECT_KYC,
  LABELS_CHAMP_KYC,
  type ChampIdentiteKyc,
  type ChampKyc,
  type DemandeKycDto,
  type DemandeRelationKycDto,
  type ProcurationClientRow,
  type ProcurationCompteRow,
  type RelationClientRow,
  type SignataireCompteRow,
  type TypeModificationKyc,
} from '../../../Models/DemandeKyc';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor = theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': { backgroundColor: emphasize(backgroundColor, 0.06) },
    '&:active': { boxShadow: theme.shadows[1], backgroundColor: emphasize(backgroundColor, 0.12) },
  };
}) as typeof Chip;

const isDateChamp = (champ: ChampKyc) => CHAMPS_DATE_KYC.includes(champ);

// Un seul champ source par Champ KYC (parfois plusieurs casses possibles) :
// les champs client viennent de GetClient, NOMCPT/TYPSIGNATURE de GetCompte.
const CLIENT_FIELD_CANDIDATES: Partial<Record<ChampKyc, string[]>> = {
  TELCLI: ['telCli', 'TELCLI', 'TelCli'],
  TEL2: ['Tel2', 'tel2', 'TEL2'],
  NOMCLI: ['NOMCLI'],
  PRECLI: ['PRECLI', 'precli'],
  DATNAI: ['DATNAI', 'datnai'],
  SITFAM: ['SITFAM', 'sitfam'],
  PROFESION: ['profesion', 'PROFESION'],
  ADRPOSTAL: ['adrPostal', 'ADRPOSTAL'],
  RUECLI: ['rueCli', 'RUECLI'],
  NOMQTIER: ['nomQtier', 'NOMQTIER'],
  VILCLI: ['vilcli', 'VILCLI'],
  BPCLI: ['BpCli', 'BPCLI'],
  EMAILCLI: ['EmailCli', 'EMAILCLI'],
  NUMCNI: ['numCNI', 'NUMCNI'],
  TYPEPIECE: ['TYPEPIECE', 'typepiece'],
  DATEDELCNI: ['DateDelcni', 'DATEDELCNI'],
  NIU: ['NIU', 'niu'],
  // 28 nouveaux champs (2026-09-23) — noms de clés devinés par analogie avec
  // les 18 ci-dessus (le même champ SQL apparaît tantôt en camelCase, tantôt
  // en MAJUSCULES selon l'endpoint) : à corriger si GetClient renvoie autre
  // chose (l'ancienne valeur resterait vide dans ce cas, sans casser la saisie).
  TYPADR: ['TYPADR', 'typAdr', 'TypAdr'],
  FAXCLI: ['FaxCli', 'FAXCLI', 'faxCli'],
  ADRDOM: ['NumDomCli', 'ADRDOM', 'adrDom'],
  TITCLI: ['TITCLI', 'titCli', 'TitCli'],
  CLSEXE: ['CLSEXE', 'clsexe'],
  ACTIONAIRE: ['ACTIONAIRE', 'actionaire'],
  LIENAI: ['LIENAI', 'lienai'],
  CDGEST: ['CDGEST', 'cdgest'],
  CODPROSPECT: ['codProspect', 'CODPROSPECT'],
  CLARIS: ['CLARIS', 'claris'],
  CDPAYS: ['CDPAYS', 'cdpays'],
  CDENAE: ['CDENAE', 'cdenae'],
  CDENUI: ['CDENUI', 'cdenui'],
  CDERES: ['CDERES', 'cderes'],
  CATPRO: ['CATPRO', 'catpro'],
  RESIDE: ['RESIDE', 'reside'],
  INSTRU: ['INSTRU', 'instru'],
  ETHNIE: ['ETHNIE', 'ethnie'],
  REGMAT: ['REGMAT', 'regmat'],
  NBRENFANT: ['nbrEnfant', 'NBRENFANT'],
  NBRPERSCHARGE: ['nbrPersCharge', 'NBRPERSCHARGE'],
  NOMEMP: ['NOMEMP', 'nomEmp'],
  MATEMP: ['MATEMP', 'matEmp'],
  ADREMP: ['ADREMP', 'adrEmp'],
  NOCNPS: ['NOCNPS', 'nocnps'],
  NOMPERE: ['nompere', 'NOMPERE'],
  NOMMERE: ['nommere', 'NOMMERE'],
  REVENUCLI: ['revenuCli', 'REVENUCLI'],
  // Confirmé par le backend (sp_SelectClient) : casse exacte Photo/Signature.
  PHOTO: ['Photo'],
  SIGNATURE: ['Signature'],
};

const COMPTE_FIELD_CANDIDATES: Partial<Record<ChampKyc, string[]>> = {
  // NOMCPT n'est plus modifiable (2026-09-28, recalculé par le backend) — la
  // candidate reste ici uniquement pour que l'ancienne valeur continue de
  // s'afficher correctement partout où elle est encore montrée en lecture.
  NOMCPT: ['NOMCPT', 'nomCpt', 'NomCpt'],
  TYPSIGNATURE: ['typSignature', 'TYPSIGNATURE', 'TypSignature'],
  // Casse exacte confirmée (src/Models/Comptes.ts / composant partagé Tsignature.tsx).
  SIGN1: ['Sign1'], SIGN2: ['Sign2'], SIGN3: ['Sign3'], SIGN4: ['Sign4'], SIGN5: ['Sign5'],
  TSIGN1: ['TSign1'], TSIGN2: ['TSign2'], TSIGN3: ['TSign3'], TSIGN4: ['TSign4'],
  // Téléphone du compte (2026-09-28) — casse exacte confirmée (src/Models/Comptes.ts : TelMobil).
  TELMOBIL: ['TelMobil', 'TELMOBIL', 'telMobil'],
};

// Opérateur entre deux emplacements de signataire — uniquement "/" (ou) ou
// "&" (et), défaut "/" si non renseigné (règle backend confirmée).
const OPERATEURS_SIGNATURE = [
  { value: '/', label: 'OU ( / )' },
  { value: '&', label: 'ET ( & )' },
];

// Tous les emplacements/opérateurs, y compris SIGN5/TSIGN4 (jamais exposés
// dans cet écran) — utilisé uniquement pour peupler l'affichage lecture
// seule du composant partagé Tsignature.tsx, qui montre toujours les 5
// emplacements, et pour l'affichage informatif de SIGN1-4 (non modifiables
// ici : leur ajout/retrait passe par "Signataires actuels").
const CHAMPS_SIGNATURE_AFFICHAGE: ChampKyc[] = ['SIGN1', 'SIGN2', 'SIGN3', 'SIGN4', 'SIGN5', 'TSIGN1', 'TSIGN2', 'TSIGN3', 'TSIGN4'];

// Groupes de champs modifiables — plus de sélecteur de type : tout est visible
// d'un coup, l'utilisateur ne remplit que ce qu'il veut changer. Chaque groupe
// rempli (au moins 1 champ) devient sa propre demande envoyée. Les groupes
// "client" s'affichent dans l'onglet Demande, les groupes "compte" dans
// l'onglet Compte (une fois un compte sélectionné).
const GROUPES_DEMANDE: { type: TypeModificationKyc; title: string; requiresCompte: boolean }[] = [
  { type: 'IDENTITE_CLIENT', title: 'Identité', requiresCompte: false },
  { type: 'TELEPHONE', title: 'Téléphone', requiresCompte: false },
  { type: 'COORDONNEES_CLIENT', title: 'Coordonnées', requiresCompte: false },
  { type: 'PIECE_IDENTITE', title: "Pièce d'identité", requiresCompte: false },
  { type: 'PROFIL_CLIENT', title: 'Profil complémentaire', requiresCompte: false },
  { type: 'EMPLOYEUR_FAMILLE', title: 'Employeur & famille', requiresCompte: false },
  { type: 'PHOTO_SIGNATURE', title: 'Photo & signature', requiresCompte: false },
  // NOM_COMPTE retiré (2026-09-28) : n'est plus modifiable via le KYC —
  // recalculé automatiquement par le backend à partir du Nom/Prénom du
  // client (toute demande contenant Champ='NOMCPT' est désormais rejetée).
  { type: 'TYPE_SIGNATURE', title: 'Type de signature', requiresCompte: true },
  { type: 'TELEPHONE_COMPTE', title: 'Téléphone du compte', requiresCompte: true },
];
const GROUPES_CLIENT = GROUPES_DEMANDE.filter((g) => !g.requiresCompte);
const GROUPES_COMPTE = GROUPES_DEMANDE.filter((g) => g.requiresCompte);

export default function KycPage() {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const [resetKey, setResetKey] = useState(0);
  const [tab, setTab] = useState('0');
  const [demandeForm] = Form.useForm();
  const [busy, setBusy] = useState(false);

  // Opérateurs ET/OU du compte : Select « manuel » (comme PHOTO/SIGNATURE),
  // pas un Form.Item direct — Form.useWatch permet d'en afficher la valeur
  // courante sans state React dédié. Les emplacements signataire (SIGN2-4),
  // eux, s'affichent en lecture seule + bouton Ajouter/Retirer (pas de Select
  // libre) — cf. bloc TYPE_SIGNATURE plus bas, pas besoin de les watcher ici.
  const signatureOperateurs: Partial<Record<ChampKyc, string>> = {
    TSIGN1: Form.useWatch('NouvelleValeur_TSIGN1', demandeForm),
    TSIGN2: Form.useWatch('NouvelleValeur_TSIGN2', demandeForm),
    TSIGN3: Form.useWatch('NouvelleValeur_TSIGN3', demandeForm),
  };

  const [numcli, setNumcli] = useState('');
  const [clientInfo, setClientInfo] = useState<any | null>(null);
  const [numcpt, setNumcpt] = useState('');
  const [compteInfo, setCompteInfo] = useState<any | null>(null);

  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [docsDemandeIds, setDocsDemandeIds] = useState<(number | string)[]>([]);

  const [relationsClient, setRelationsClient] = useState<RelationClientRow[]>([]);
  const [procurationsClient, setProcurationsClient] = useState<ProcurationClientRow[]>([]);
  const [signataires, setSignataires] = useState<SignataireCompteRow[]>([]);
  const [procurations, setProcurations] = useState<ProcurationCompteRow[]>([]);
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [showAjoutSignataire, setShowAjoutSignataire] = useState(false);
  const [showAjoutProcuration, setShowAjoutProcuration] = useState(false);
  const [ajoutSignataireCodRel, setAjoutSignataireCodRel] = useState<string | undefined>();
  const [ajoutSignatairePlafond, setAjoutSignatairePlafond] = useState<number | null>(null);
  const [ajoutProcurationCodRel, setAjoutProcurationCodRel] = useState<string | undefined>();
  const [ajoutProcurationPlafond, setAjoutProcurationPlafond] = useState<number | null>(null);
  // "Retirer" ne fait que marquer une fiche existante — comme pour l'ajout,
  // le retrait n'est envoyé qu'au clic sur « Enregistrer ».
  const [retraitSignataireCodRel, setRetraitSignataireCodRel] = useState<string | undefined>();
  const [retraitProcurationCodRel, setRetraitProcurationCodRel] = useState<string | undefined>();
  // « Modifier » (identité propre du signataire/mandataire, pas le lien) —
  // demande séparée, soumise directement depuis sa propre modale (pas via le
  // bouton « Enregistrer » global, contrairement à ajout/retrait).
  const [modifierIdentite, setModifierIdentite] = useState<{
    type: 'SIGNATAIRE' | 'PROCURATION';
    codRel: string;
    ancienneValeurs: Partial<Record<ChampIdentiteKyc, string>>;
  } | null>(null);
  const [modeEditionClient, setModeEditionClient] = useState(false);
  const [modeEditionCompte, setModeEditionCompte] = useState(false);

  const clientCni = getVal(clientInfo, 'numCNI', 'NUMCNI');
  const relationsClientFiltrees = relationsClient.filter((r) => !clientCni || r.cni !== clientCni);
  const procurationsClientFiltrees = procurationsClient.filter((r) => !clientCni || r.cni !== clientCni);

  function getAncienneValeur(champ: ChampKyc): string {
    const compteKeys = COMPTE_FIELD_CANDIDATES[champ];
    const source = compteKeys ? compteInfo : clientInfo;
    const keys = compteKeys ?? CLIENT_FIELD_CANDIDATES[champ] ?? [];
    let value = getVal(source, ...keys) ?? '';
    if (isDateChamp(champ) && value) value = String(value).slice(0, 10);
    // Un opérateur ET/OU jamais renseigné vaut "/" par défaut en base (valeur
    // réellement active, pas une simple valeur d'affichage) — sans ce repli,
    // l'ancienne valeur envoyée serait "" alors que "/" est déjà la valeur
    // réelle, ce qui ferait apparaître un changement à tort dans la validation.
    if (champ.startsWith('TSIGN') && !value) value = '/';
    return value;
  }

  // Les 13 champs picklist ont leur libellé humain renvoyé par GetClient sous
  // "lib_<CHAMP>" (même casse que le code) — confirmé par le backend. Absent
  // pour certains types de client (ex. REGMAT/CATPRO/ETHNIE/INSTRU sur un
  // client Moral) : getVal renvoie alors undefined, sans planter.
  function getAncienneLibelle(champ: ChampKyc): string | undefined {
    return getVal(clientInfo, `lib_${champ}`);
  }

  // CODREL -> "Nom Prénom" pour affichage — recherché parmi les relations
  // connues du client ; si introuvable (ex. relation d'un autre client, ou
  // liste pas encore chargée), affiche simplement le code brut.
  function libelleSignataireSlot(codRel: string): string {
    const personne = relationsClientFiltrees.find((r) => r.CODREL === codRel);
    return personne ? `${personne.NomRel} ${personne.Prenom}`.trim() : codRel;
  }

  // Détermine comment un champ doit s'afficher : image (upload+aperçu),
  // picklist (SearchModal), choix fixe (radio) ou texte/date libre — selon
  // les champs client ajoutés le 2026-09-23, sinon comportement inchangé.
  function champFieldProps(champ: ChampKyc) {
    if (CHAMPS_IMAGE_KYC.includes(champ)) return { type: 'image' as const, form: demandeForm };
    const picklist = CHAMPS_SELECT_KYC[champ];
    if (picklist) return { type: 'select' as const, picklist, form: demandeForm, ancienneLibelle: getAncienneLibelle(champ) };
    const radioOptions = CHAMPS_RADIO_KYC[champ];
    if (radioOptions) return { type: 'radio' as const, radioOptions, form: demandeForm };
    return { type: isDateChamp(champ) ? ('date' as const) : ('texte' as const) };
  }

  // Chaque champ affiche directement sa valeur actuelle dans l'input (pas
  // juste au-dessus) — resetFields() seul rétablirait une valeur figée au
  // moment du montage, donc on la resynchronise explicitement au changement
  // de client/compte, une fois le rendu à jour. Deux effets séparés (pas un
  // seul sur [clientInfo, compteInfo]) : sinon choisir un compte APRÈS avoir
  // déjà modifié des champs client écrasait ces modifications, puisque l'un
  // remettait aussi les champs de l'autre à leur ancienne valeur.
  useEffect(() => {
    const valeurs: Record<string, string> = {};
    GROUPES_CLIENT.forEach((g) => {
      CHAMPS_PAR_TYPE_MODIFICATION[g.type].forEach((champ) => {
        valeurs[`NouvelleValeur_${champ}`] = getAncienneValeur(champ);
      });
    });
    demandeForm.setFieldsValue(valeurs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo]);

  useEffect(() => {
    const valeurs: Record<string, string> = {};
    GROUPES_COMPTE.forEach((g) => {
      CHAMPS_PAR_TYPE_MODIFICATION[g.type].forEach((champ) => {
        valeurs[`NouvelleValeur_${champ}`] = getAncienneValeur(champ);
      });
    });
    // Le composant partagé Tsignature.tsx attend ses propres noms de champ
    // (Sign1..Sign5, TSign1..TSign4, sans préfixe) pour son affichage lecture
    // seule — en plus des NouvelleValeur_* utilisés pour la saisie/le diff.
    // Tous les emplacements (y compris SIGN1/SIGN5/TSIGN4, non éditables ici)
    // car ce composant les affiche tous.
    CHAMPS_SIGNATURE_AFFICHAGE.forEach((champ) => {
      valeurs[COMPTE_FIELD_CANDIDATES[champ]![0]] = getAncienneValeur(champ);
    });
    demandeForm.setFieldsValue(valeurs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compteInfo]);

  function onClientChange(newNumcli: string, info: any | null) {
    setNumcli(newNumcli);
    setClientInfo(info);
    setNumcpt('');
    setCompteInfo(null);
    setSignataires([]);
    setProcurations([]);
    demandeForm.resetFields();
    setShowAjoutSignataire(false);
    setShowAjoutProcuration(false);
    setAjoutSignataireCodRel(undefined);
    setAjoutSignatairePlafond(null);
    setAjoutProcurationCodRel(undefined);
    setAjoutProcurationPlafond(null);
    setRetraitSignataireCodRel(undefined);
    setRetraitProcurationCodRel(undefined);
    setModifierIdentite(null);
    setModeEditionClient(false);
    setModeEditionCompte(false);
    if (newNumcli) {
      Promise.all([
        fetchList<RelationClientRow>(Data.ApiUrl.KycRelationsClient, 'CypherNumcli', newNumcli),
        fetchList<ProcurationClientRow>(Data.ApiUrl.KycProcurationsClient, 'CypherNumcli', newNumcli),
      ]).then(([rel, proc]) => {
        setRelationsClient(rel);
        setProcurationsClient(proc);
      });
    } else {
      setRelationsClient([]);
      setProcurationsClient([]);
    }
  }

  async function consultRelationsCompte(currentNumcpt: string) {
    if (!currentNumcpt) return;
    setLoadingRelations(true);
    try {
      const [signRows, procRows] = await Promise.all([
        fetchList<SignataireCompteRow>(Data.ApiUrl.KycSignatairesCompte, 'CypherNumcpt', currentNumcpt),
        fetchList<ProcurationCompteRow>(Data.ApiUrl.KycProcurationsCompte, 'CypherNumcpt', currentNumcpt),
      ]);
      setSignataires(signRows);
      setProcurations(procRows);
    } finally {
      setLoadingRelations(false);
    }
  }

  function onCompteChange(newNumcpt: string, info: any | null) {
    setNumcpt(newNumcpt);
    setCompteInfo(info);
    // resetFields() sans argument réinitialise TOUT le formulaire, y compris
    // les champs client déjà modifiés — on ne doit réinitialiser que les
    // champs compte ici (le client n'a pas changé, ses saisies doivent rester).
    demandeForm.resetFields(GROUPES_COMPTE.flatMap((g) => CHAMPS_PAR_TYPE_MODIFICATION[g.type]).map((champ) => `NouvelleValeur_${champ}`));
    demandeForm.resetFields(CHAMPS_SIGNATURE_AFFICHAGE.map((champ) => COMPTE_FIELD_CANDIDATES[champ]![0]));
    setShowAjoutSignataire(false);
    setShowAjoutProcuration(false);
    setAjoutSignataireCodRel(undefined);
    setAjoutSignatairePlafond(null);
    setAjoutProcurationCodRel(undefined);
    setAjoutProcurationPlafond(null);
    setRetraitSignataireCodRel(undefined);
    setRetraitProcurationCodRel(undefined);
    setModifierIdentite(null);
    setModeEditionCompte(false);
    if (newNumcpt) consultRelationsCompte(newNumcpt);
    else { setSignataires([]); setProcurations([]); }
  }

  async function flushStagedFiles(idDemande: number | string): Promise<number> {
    let echecs = 0;
    for (const staged of stagedFiles) {
      const result = await uploadKycDocument(idDemande, user.coduti, staged.file);
      if (!result.ok) echecs += 1;
    }
    return echecs;
  }

  async function envoyer(dto: DemandeKycDto | DemandeRelationKycDto, endpoint: string): Promise<number | string | null> {
    try {
      const response = await api.post(endpoint, encryptBody(dto), { headers: { 'Content-Type': 'application/json' } });
      const payload = parseEncryptedResponse(response);
      if (payload && payload.StatutCode === 200) return payload.Result;
      Swal.fire({ title: 'Erreur', text: payload?.Message ?? 'Une demande a été refusée.', icon: 'error' });
      return null;
    } catch (error: any) {
      const payload = parseEncryptedResponse(error?.response);
      Swal.fire({ title: 'Erreur', text: payload?.Message ?? "Impossible de soumettre une demande.", icon: 'error' });
      return null;
    }
  }

  // Plus de sélecteur de type, plus un appel par catégorie : le backend
  // accepte désormais un type générique (MODIFICATION_CLIENT_COMPTE) qui
  // mélange champs client et compte dans un seul Details[] — tous les champs
  // touchés, toutes catégories confondues, partent en une seule demande.
  // Signataire/procuration restent des demandes séparées (endpoint dédié).
  async function onSubmitDemande(values: any) {
    // Chaque input affiche déjà l'ancienne valeur — un champ n'est "touché"
    // que si sa valeur diffère réellement de celle-ci, pas juste non vide.
    const champEstModifie = (champ: ChampKyc) => {
      const nouvelle = values[`NouvelleValeur_${champ}`];
      if (!nouvelle) return false;
      return String(nouvelle) !== String(getAncienneValeur(champ));
    };

    const groupesTouches = GROUPES_DEMANDE.filter((g) => !g.requiresCompte || numcpt);
    const champsTouches = groupesTouches
      .flatMap((g) => CHAMPS_PAR_TYPE_MODIFICATION[g.type])
      .filter(champEstModifie);
    const details = champsTouches.map((champ) => ({
      Champ: champ,
      AncienneValeur: getAncienneValeur(champ) || undefined,
      NouvelleValeur: values[`NouvelleValeur_${champ}`],
    }));
    const categoriesTouchees = groupesTouches
      .filter((g) => CHAMPS_PAR_TYPE_MODIFICATION[g.type].some(champEstModifie))
      .map((g) => g.title);

    // Une personne choisie ET un plafond réellement saisi (> 0) suffisent —
    // pas de case à cocher séparée : sélectionner une fiche sans indiquer de
    // plafond (ou le laisser à 0/vide) ne déclenche rien automatiquement.
    const ajoutSignataire = numcpt && ajoutSignataireCodRel && ajoutSignatairePlafond !== null && ajoutSignatairePlafond > 0 ? (() => {
      const personne = relationsClientFiltrees.find((r) => r.CODREL === ajoutSignataireCodRel);
      return { label: `Ajout signataire : ${personne?.NomRel ?? ''} ${personne?.Prenom ?? ''}`.trim(), codRel: ajoutSignataireCodRel, plafond: ajoutSignatairePlafond };
    })() : null;

    const ajoutProcuration = numcpt && ajoutProcurationCodRel && ajoutProcurationPlafond !== null && ajoutProcurationPlafond > 0 ? (() => {
      const personne = procurationsClientFiltrees.find((p) => p.Refprocur === ajoutProcurationCodRel);
      return { label: `Ajout procuration : ${personne?.nom ?? ''} ${personne?.prenom ?? ''}`.trim(), codRel: ajoutProcurationCodRel, plafond: ajoutProcurationPlafond };
    })() : null;

    // Fiches marquées via le bouton « Retirer » des tableaux Signataires /
    // Procurations actuel(le)s — le retrait n'a été que sélectionné, il part
    // maintenant avec le reste au même titre qu'un ajout.
    const retraitSignataire = numcpt && retraitSignataireCodRel ? (() => {
      const personne = signataires.find((s) => s.CODREL === retraitSignataireCodRel);
      return { label: `Retrait signataire : ${personne?.NomRel ?? ''} ${personne?.Prenom ?? ''}`.trim(), codRel: retraitSignataireCodRel };
    })() : null;

    const retraitProcuration = numcpt && retraitProcurationCodRel ? (() => {
      const personne = procurations.find((p) => p.CODREL === retraitProcurationCodRel);
      return { label: `Retrait procuration : ${personne?.nom ?? ''} ${personne?.prenom ?? ''}`.trim(), codRel: retraitProcurationCodRel };
    })() : null;

    if (details.length === 0 && !ajoutSignataire && !ajoutProcuration && !retraitSignataire && !retraitProcuration) {
      Swal.fire({ title: 'Attention', text: 'Veuillez renseigner au moins un champ, un ajout ou un retrait à enregistrer.', icon: 'warning' });
      return;
    }

    const libelles = [...categoriesTouchees, ajoutSignataire?.label, ajoutProcuration?.label, retraitSignataire?.label, retraitProcuration?.label].filter(Boolean);

    const confirmation = await Swal.fire({
      title: 'Soumettre la demande KYC',
      text: `Confirmez-vous l'envoi de : ${libelles.join(', ')} pour le client ${numcli} ?`,
      showCancelButton: true,
      color: 'green',
      confirmButtonText: 'Oui',
      confirmButtonColor: 'green',
      cancelButtonColor: 'darkred',
      cancelButtonText: 'Non',
      showLoaderOnConfirm: true,
      icon: 'question',
    });
    if (!confirmation.isConfirmed) return;

    setBusy(true);
    try {
      const idsCreated: (number | string)[] = [];
      // Une seule demande peut désormais porter à la fois les champs modifiés
      // et un ajout/retrait signataire/procuration (bloc Relation) — un seul
      // appel, un seul IdDemande. S'il y a exceptionnellement plusieurs
      // actions relation (ex. ajout signataire ET retrait procuration dans la
      // même saisie), la première voyage avec les champs, les suivantes
      // partent en complément (Details vide).
      const relationsAEnvoyer: { TypeRelation: 'SIGNATAIRE_AJOUT' | 'PROCURATION_AJOUT' | 'SIGNATAIRE_RETRAIT' | 'PROCURATION_RETRAIT'; CodRel: string; Plafond?: number }[] = [];
      if (ajoutSignataire) relationsAEnvoyer.push({ TypeRelation: 'SIGNATAIRE_AJOUT', CodRel: ajoutSignataire.codRel, Plafond: ajoutSignataire.plafond });
      if (ajoutProcuration) relationsAEnvoyer.push({ TypeRelation: 'PROCURATION_AJOUT', CodRel: ajoutProcuration.codRel, Plafond: ajoutProcuration.plafond });
      if (retraitSignataire) relationsAEnvoyer.push({ TypeRelation: 'SIGNATAIRE_RETRAIT', CodRel: retraitSignataire.codRel });
      if (retraitProcuration) relationsAEnvoyer.push({ TypeRelation: 'PROCURATION_RETRAIT', CodRel: retraitProcuration.codRel });

      if (details.length > 0 || relationsAEnvoyer.length > 0) {
        const [premiereRelation, ...autresRelations] = relationsAEnvoyer;
        const dto: DemandeKycDto = {
          NUMCLI: numcli,
          NUMCPT: numcpt || undefined,
          TypeModification: 'MODIFICATION_CLIENT_COMPTE',
          EnregistrePar: user.coduti,
          Details: details,
          Relation: premiereRelation,
        };
        const id = await envoyer(dto, Data.ApiUrl.KycDemandeModification);
        if (id) idsCreated.push(id);

        for (const relation of autresRelations) {
          const dtoSupplementaire: DemandeKycDto = {
            NUMCLI: numcli, NUMCPT: numcpt || undefined, TypeModification: 'MODIFICATION_CLIENT_COMPTE',
            EnregistrePar: user.coduti, Details: [], Relation: relation,
          };
          const idSupplementaire = await envoyer(dtoSupplementaire, Data.ApiUrl.KycDemandeModification);
          if (idSupplementaire) idsCreated.push(idSupplementaire);
        }
      }

      if (idsCreated.length > 0) {
        let echecsDocs = 0;
        for (const id of idsCreated) echecsDocs += await flushStagedFiles(id);
        setDocsDemandeIds(idsCreated);
        setStagedFiles([]);
        demandeForm.resetFields();
        setModeEditionClient(false);
        setModeEditionCompte(false);
        setShowAjoutSignataire(false);
        setAjoutSignataireCodRel(undefined);
        setAjoutSignatairePlafond(null);
        setShowAjoutProcuration(false);
        setAjoutProcurationCodRel(undefined);
        setAjoutProcurationPlafond(null);
        setRetraitSignataireCodRel(undefined);
        setRetraitProcurationCodRel(undefined);
        setModifierIdentite(null);
        if (numcpt) await consultRelationsCompte(numcpt);
        setTab('2');
        Swal.fire({
          title: 'Succès',
          text: echecsDocs > 0
            ? `${idsCreated.length} demande(s) enregistrée(s). ${echecsDocs} document(s) n'ont pas pu être joints — réessayez depuis l'onglet Documents.`
            : `${idsCreated.length} demande(s) enregistrée(s), en attente de validation.`,
          icon: echecsDocs > 0 ? 'warning' : 'success',
        });
      }
    } finally {
      setBusy(false);
    }
  }

  function onFinishFailed({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) {
    Swal.fire({ title: 'Formulaire incomplet', text: `Veuillez renseigner les champs obligatoires (${errorFields.length}).`, icon: 'warning' });
  }

  function onActualiser() {
    demandeForm.resetFields();
    Swal.fire({ title: 'Actualisé', text: 'Le formulaire a été réinitialisé.', icon: 'success', timer: 1200, showConfirmButton: false });
  }

  function onAnnuler() {
    demandeForm.resetFields();
    setNumcli('');
    setClientInfo(null);
    setNumcpt('');
    setCompteInfo(null);
    setStagedFiles([]);
    setDocsDemandeIds([]);
    setRelationsClient([]);
    setProcurationsClient([]);
    setSignataires([]);
    setProcurations([]);
    setShowAjoutSignataire(false);
    setShowAjoutProcuration(false);
    setAjoutSignataireCodRel(undefined);
    setAjoutSignatairePlafond(null);
    setAjoutProcurationCodRel(undefined);
    setAjoutProcurationPlafond(null);
    setRetraitSignataireCodRel(undefined);
    setRetraitProcurationCodRel(undefined);
    setModifierIdentite(null);
    setModeEditionClient(false);
    setModeEditionCompte(false);
    setTab('0');
    setResetKey((k) => k + 1);
  }

  // Fermer le panneau d'ajout doit aussi oublier la sélection en cours —
  // sinon une personne/plafond choisis puis abandonnés restaient en mémoire
  // et étaient soumis silencieusement au prochain "Enregistrer".
  function toggleAjoutSignataire() {
    setShowAjoutSignataire((v) => {
      if (v) {
        setAjoutSignataireCodRel(undefined);
        setAjoutSignatairePlafond(null);
          }
      return !v;
    });
  }

  function toggleAjoutProcuration() {
    setShowAjoutProcuration((v) => {
      if (v) {
        setAjoutProcurationCodRel(undefined);
        setAjoutProcurationPlafond(null);
          }
      return !v;
    });
  }

  // Photo/Signature de la personne — lues de façon défensive (plusieurs
  // casses possibles, absent sans casser l'affichage), affichées via le
  // composant partagé KycImageThumb (zoom plein écran au clic partout).
  function renderPhotoSignatureCell(src: string | undefined, alt: string) {
    return <KycImageThumb src={src} alt={alt} />;
  }

  // « Retirer » ne fait que marquer/démarquer la fiche — aucun appel réseau
  // ici, le retrait n'est envoyé qu'au clic sur « Enregistrer » (Actions),
  // comme pour un ajout.
  const signataireColumns = [
    { title: 'Nom', dataIndex: 'NomRel', key: 'NomRel' },
    { title: 'Prénom', dataIndex: 'Prenom', key: 'Prenom' },
    { title: 'CNI', dataIndex: 'cni', key: 'cni' },
    {
      title: 'Photo', key: 'photo', width: 60,
      render: (_: any, row: SignataireCompteRow) => renderPhotoSignatureCell(getVal(row, 'Photo', 'photo', 'PHOTO'), 'Photo'),
    },
    {
      title: 'Signature', key: 'signature', width: 60,
      render: (_: any, row: SignataireCompteRow) => renderPhotoSignatureCell(getVal(row, 'Signature', 'signature', 'SIGNATURE'), 'Signature'),
    },
    { title: 'Plafond', dataIndex: 'LIMITE', key: 'LIMITE', render: (v: number) => v?.toLocaleString('fr-FR') },
    {
      title: 'Action', key: 'action', align: 'right' as const,
      render: (_: any, row: SignataireCompteRow) => {
        const marque = retraitSignataireCodRel === row.CODREL;
        return (
          <Space size="small">
            <Button
              size="small"
              icon={<EditOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}
              onClick={() => setModifierIdentite({
                type: 'SIGNATAIRE',
                codRel: row.CODREL,
                ancienneValeurs: {
                  REL_NOMREL: row.NomRel, REL_PRENOM: row.Prenom, REL_CNI: row.cni,
                  REL_PHOTO: getVal(row, 'Photo', 'photo', 'PHOTO'),
                  REL_SIGNATURE: getVal(row, 'Signature', 'signature', 'SIGNATURE'),
                },
              })}
            >
              Modifier
            </Button>
            <Button
              size="small" danger={marque}
              icon={<PersonRemoveIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}
              onClick={() => setRetraitSignataireCodRel((v) => (v === row.CODREL ? undefined : row.CODREL))}
            >
              {marque ? 'Annuler le retrait' : 'Retirer'}
            </Button>
          </Space>
        );
      },
    },
  ];

  const procurationColumns = [
    { title: 'Nom', dataIndex: 'nom', key: 'nom' },
    { title: 'Prénom', dataIndex: 'prenom', key: 'prenom' },
    { title: 'CNI', dataIndex: 'cni', key: 'cni' },
    {
      title: 'Photo', key: 'photo', width: 60,
      render: (_: any, row: ProcurationCompteRow) => renderPhotoSignatureCell(getVal(row, 'Photo', 'photo', 'PHOTO'), 'Photo'),
    },
    {
      title: 'Signature', key: 'signature', width: 60,
      render: (_: any, row: ProcurationCompteRow) => renderPhotoSignatureCell(getVal(row, 'Signature', 'signature', 'SIGNATURE'), 'Signature'),
    },
    { title: 'Plafond', dataIndex: 'LIMITE', key: 'LIMITE', render: (v: number) => v?.toLocaleString('fr-FR') },
    {
      title: 'Action', key: 'action', align: 'right' as const,
      render: (_: any, row: ProcurationCompteRow) => {
        const marque = retraitProcurationCodRel === row.CODREL;
        return (
          <Space size="small">
            <Button
              size="small"
              icon={<EditOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}
              onClick={() => setModifierIdentite({
                type: 'PROCURATION',
                codRel: row.CODREL,
                ancienneValeurs: {
                  PROC_NOM: row.nom, PROC_PRENOM: row.prenom, PROC_CNI: row.cni,
                  PROC_PHOTO: getVal(row, 'Photo', 'photo', 'PHOTO'),
                  PROC_SIGNATURE: getVal(row, 'Signature', 'signature', 'SIGNATURE'),
                },
              })}
            >
              Modifier
            </Button>
            <Button
              size="small" danger={marque}
              icon={<PersonRemoveIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}
              onClick={() => setRetraitProcurationCodRel((v) => (v === row.CODREL ? undefined : row.CODREL))}
            >
              {marque ? 'Annuler le retrait' : 'Retirer'}
            </Button>
          </Space>
        );
      },
    },
  ];

  const items: TabsProps['items'] = [
    {
      key: '0',
      label: <span><FileTextOutlined /> Demande</span>,
      children: (
        <Spin spinning={busy} size="large">
          <KycClientHeader key={resetKey} onClientChange={onClientChange} />
          {!numcli ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>Sélectionnez un client ci-dessus pour commencer une demande.</Typography>
          ) : (
            <>
              {getVal(clientInfo, 'NbModificationsKyc') !== undefined && (
                <Chip
                  size="small" color="info" variant="outlined" sx={{ mb: 1 }}
                  label={`Modifié ${getVal(clientInfo, 'NbModificationsKyc')} fois (KYC validés)`}
                />
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button icon={<EditOutlined />} onClick={() => setModeEditionClient((v) => !v)}>
                  {modeEditionClient ? 'Annuler la modification' : 'Modifier'}
                </Button>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, alignItems: 'stretch' }}>
                {GROUPES_CLIENT.map((g) => (
                  <SectionCard key={g.type} icon={<FileTextOutlined style={{ fontSize: 14 }} />} title={g.title} color="#124b7a">
                    {CHAMPS_PAR_TYPE_MODIFICATION[g.type].map((champ) => (
                      <EditableValueField
                        key={champ}
                        label={LABELS_CHAMP_KYC[champ]}
                        name={`NouvelleValeur_${champ}`}
                        ancienneValeur={getAncienneValeur(champ)}
                        editable={modeEditionClient}
                        {...champFieldProps(champ)}
                      />
                    ))}
                  </SectionCard>
                ))}
              </Box>
            </>
          )}
        </Spin>
      ),
    },
    {
      key: '1',
      label: <span><CreditCardOutlined /> Compte</span>,
      disabled: !numcli,
      children: (
        <Spin spinning={loadingRelations || busy} size="large">
          <KycComptesPanel numcli={numcli} numcpt={numcpt} onCompteChange={onCompteChange} />
          {numcpt && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, mt: 2 }}>
                <Button icon={<EditOutlined />} onClick={() => setModeEditionCompte((v) => !v)}>
                  {modeEditionCompte ? 'Annuler la modification' : 'Modifier'}
                </Button>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, alignItems: 'stretch' }}>
                {GROUPES_COMPTE.map((g) => {
                  if (g.type !== 'TYPE_SIGNATURE') {
                    return (
                      <SectionCard key={g.type} icon={<CreditCardOutlined style={{ fontSize: 14 }} />} title={g.title} color="#124b7a">
                        {CHAMPS_PAR_TYPE_MODIFICATION[g.type].map((champ) => (
                          <EditableValueField
                            key={champ}
                            label={LABELS_CHAMP_KYC[champ]}
                            name={`NouvelleValeur_${champ}`}
                            ancienneValeur={getAncienneValeur(champ)}
                            editable={modeEditionCompte}
                            {...champFieldProps(champ)}
                          />
                        ))}
                      </SectionCard>
                    );
                  }

                  // Formule ET/OU entre signataires — même composant partagé
                  // (Tsignature.tsx) qu'ailleurs dans l'appli (ex.
                  // /situationclient/vu360) en lecture seule. En édition, seuls
                  // les opérateurs (TSIGN1-3) sont modifiables : les emplacements
                  // (SIGN1-4) restent en lecture seule — leur ajout/retrait passe
                  // déjà par le tableau "Signataires actuels" plus bas, pas de
                  // second mécanisme ici pour la même chose.
                  const renderEmplacementSignature = (champ: ChampKyc) => (
                    <Box key={champ} sx={{ p: 1, borderRadius: 1.5, border: '1px dashed', borderColor: 'grey.300', bgcolor: 'grey.50', minWidth: 170 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{LABELS_CHAMP_KYC[champ]}</Typography>
                      <Typography variant="body2" fontWeight={600} color={getAncienneValeur(champ) ? 'text.primary' : 'text.secondary'}>
                        {getAncienneValeur(champ) ? libelleSignataireSlot(getAncienneValeur(champ)) : '— vide —'}
                      </Typography>
                    </Box>
                  );
                  const renderOperateurSignature = (champ: ChampKyc) => (
                    <Fragment key={champ}>
                      <Form.Item name={`NouvelleValeur_${champ}`} noStyle><Input type="hidden" /></Form.Item>
                      <Select
                        style={{ width: 90 }}
                        value={signatureOperateurs[champ] || '/'}
                        onChange={(v) => demandeForm.setFieldValue(`NouvelleValeur_${champ}`, v)}
                        options={OPERATEURS_SIGNATURE}
                      />
                    </Fragment>
                  );

                  return (
                    <SectionCard key={g.type} icon={<CreditCardOutlined style={{ fontSize: 14 }} />} title={g.title} color="#124b7a">
                      {!modeEditionCompte ? (
                        <TypSignature width={3} widthCode="15%" Dissebled={true} />
                      ) : (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                          {renderEmplacementSignature('SIGN1')}
                          {renderOperateurSignature('TSIGN1')}
                          {renderEmplacementSignature('SIGN2')}
                          {renderOperateurSignature('TSIGN2')}
                          {renderEmplacementSignature('SIGN3')}
                          {renderOperateurSignature('TSIGN3')}
                          {renderEmplacementSignature('SIGN4')}
                        </Stack>
                      )}
                    </SectionCard>
                  );
                })}
              </Box>

              <SectionCard
                icon={<TeamOutlined style={{ fontSize: 14 }} />} title="Signataires actuels" color="#2e7d32"
                action={<Button size="small" className="bt_add" style={{ color: '#fff' }} icon={<UserAddOutlined />} onClick={toggleAjoutSignataire}>{showAjoutSignataire ? 'Fermer' : 'Ajouter'}</Button>}
              >
                <Table
                  rowKey="CODREL" size="small" pagination={false} bordered dataSource={signataires} columns={signataireColumns as any}
                  locale={{ emptyText: 'Aucun signataire pour ce compte.' }}
                  onRow={(row) => ({ style: row.CODREL === retraitSignataireCodRel ? { backgroundColor: '#fff1f0' } : undefined })}
                />
                {retraitSignataireCodRel && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    Ce retrait sera envoyé avec le bouton « Enregistrer » (Actions).
                  </Typography>
                )}
                {showAjoutSignataire && (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'success.50', border: '1px dashed', borderColor: 'success.light' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <Box sx={{ flex: 1, minWidth: 260 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Personne (fiche existante)</Typography>
                        <Select
                          style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="Sélectionner"
                          value={ajoutSignataireCodRel} onChange={setAjoutSignataireCodRel}
                          options={relationsClientFiltrees.map((r) => ({ value: r.CODREL, label: `${r.NomRel} ${r.Prenom}${r.cni ? ` — ${r.cni}` : ''}` }))}
                          notFoundContent="Aucune fiche disponible."
                        />
                      </Box>
                      <Box sx={{ width: 160 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Plafond</Typography>
                        <InputNumber style={{ width: '100%' }} min={0} value={ajoutSignatairePlafond} onChange={setAjoutSignatairePlafond} />
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Cet ajout sera envoyé avec le bouton « Enregistrer » (Actions).
                    </Typography>
                  </Box>
                )}
              </SectionCard>

              <SectionCard
                icon={<SafetyCertificateOutlined style={{ fontSize: 14 }} />} title="Procurations actuelles" color="#e65100"
                action={<Button size="small" className="bt_add" style={{ color: '#fff' }} icon={<UserAddOutlined />} onClick={toggleAjoutProcuration}>{showAjoutProcuration ? 'Fermer' : 'Ajouter'}</Button>}
              >
                <Table
                  rowKey="CODREL" size="small" pagination={false} bordered dataSource={procurations} columns={procurationColumns as any}
                  locale={{ emptyText: 'Aucune procuration pour ce compte.' }}
                  onRow={(row) => ({ style: row.CODREL === retraitProcurationCodRel ? { backgroundColor: '#fff1f0' } : undefined })}
                />
                {retraitProcurationCodRel && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    Ce retrait sera envoyé avec le bouton « Enregistrer » (Actions).
                  </Typography>
                )}
                {showAjoutProcuration && (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'warning.50', border: '1px dashed', borderColor: 'warning.light' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <Box sx={{ flex: 1, minWidth: 260 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Personne (fiche existante)</Typography>
                        <Select
                          style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="Sélectionner"
                          value={ajoutProcurationCodRel} onChange={setAjoutProcurationCodRel}
                          options={procurationsClientFiltrees.map((p) => ({ value: p.Refprocur, label: `${p.nom} ${p.prenom}${p.cni ? ` — ${p.cni}` : ''}` }))}
                          notFoundContent="Aucune fiche disponible."
                        />
                      </Box>
                      <Box sx={{ width: 160 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Plafond</Typography>
                        <InputNumber style={{ width: '100%' }} min={0} value={ajoutProcurationPlafond} onChange={setAjoutProcurationPlafond} />
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Cet ajout sera envoyé avec le bouton « Enregistrer » (Actions).
                    </Typography>
                  </Box>
                )}
              </SectionCard>
            </>
          )}
        </Spin>
      ),
    },
    {
      key: '2',
      label: <span><PaperClipOutlined /> Document</span>,
      disabled: !numcli,
      children: docsDemandeIds.length > 0 ? (
        <Stack spacing={2}>
          {docsDemandeIds.map((id) => (
            <SectionCard key={id} icon={<PaperClipOutlined style={{ fontSize: 14 }} />} title={`Pièces jointes — demande n°${id}`} color="#2e7d32">
              <KycDocumentsPanel idDemande={id} uploadePar={user.coduti} />
            </SectionCard>
          ))}
          <Button onClick={onAnnuler}>Composer une nouvelle demande</Button>
        </Stack>
      ) : (
        <SectionCard icon={<PaperClipOutlined style={{ fontSize: 14 }} />} title="Documents justificatifs" subtitle="Ajoutés maintenant, ils seront envoyés dès la soumission de la demande (onglet Demande)" color="#0277bd">
          <StagedDocumentsPicker files={stagedFiles} onChange={setStagedFiles} />
        </SectionCard>
      ),
    },
  ];

  return (
    <div className="tab">
      <Box className="formTitle">
        <Breadcrumbs aria-label="breadcrumb">
          <StyledBreadcrumb component="a" href="/" label="Home" icon={<HomeIcon fontSize="small" />} />
          <StyledBreadcrumb component="a" href="#" label="Gestion" />
          <StyledBreadcrumb label="KYC" deleteIcon={<ExpandMoreIcon />} />
        </Breadcrumbs>
      </Box>

      <div className="clientP">
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          backgroundColor: 'var(--primaryColor)',
          color: 'var(--white)',
          padding: '8px 16px',
          borderRadius: 1,
          borderBottom: '5px solid var(--darkColor)',
          whiteSpace: 'nowrap',
        }}>
          <SafetyCertificateOutlined style={{ color: 'var(--white)' }} />
          <Typography fontWeight={600} sx={{ color: 'var(--white)' }}>Connaissance client (KYC)</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              fontStyle="inherit"
              className="border"
              sx={{ p: 1, width: '100%', maxHeight: '75vh', overflowY: 'auto', boxSizing: 'border-box' }}
            >
              <Form form={demandeForm} labelAlign="left" autoComplete="off" onFinish={onSubmitDemande} onFinishFailed={onFinishFailed}>
                <AntTabs
                  size="large" type="card" activeKey={tab} items={items}
                  onChange={(key) => { if (key !== '0' && !numcli) return; setTab(key); }}
                />
              </Form>
            </Box>
          </Box>

          <div
            className="bG"
            style={{
              flex: '0 0 220px',
              width: 220,
              position: 'relative',
              left: 'auto',
              top: 'auto',
              marginTop: 0,
              boxSizing: 'border-box',
            }}
          >
            <legend><Typography color="darkgreen">Actions</Typography></legend>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                size="large" className="bt_add" type="ghost" htmlType="button" block
                disabled={!numcli}
                onClick={() => demandeForm.submit()}
              >
                Enregistrer
              </Button>
              <Button
                size="large" className="bt_add" type="ghost" htmlType="button" block
                disabled={!numcli}
                onClick={onActualiser}
              >
                Actualiser
              </Button>
              <Button
                size="large" className="bt_add" type="ghost" htmlType="button" block
                onClick={onAnnuler}
              >
                Annuler
              </Button>
            </Space>
          </div>
        </Box>
      </div>

      {modifierIdentite && (
        <KycModifierIdentiteModal
          open={Boolean(modifierIdentite)}
          type={modifierIdentite.type}
          numcli={numcli}
          codRel={modifierIdentite.codRel}
          ancienneValeurs={modifierIdentite.ancienneValeurs}
          enregistrePar={user.coduti}
          onClose={() => setModifierIdentite(null)}
          onSubmitted={() => setModifierIdentite(null)}
        />
      )}
    </div>
  );
}
